import { useQuery } from '@apollo/client/react'
import { dedupe } from '@/helpers/dedupe'
import { Table as BaseTable, type TableProps } from '../table'
import { usePageInfo } from '@/hooks/usePageInfo'
import { Slimline as Error } from '@/components/error'
import { LoadingSkeleton } from '@/components/base/loading-skeleton'
import { graphql } from '@/gql'
import type { ItemTestStepResultFilters, ItemTestStepResultOrder } from '@/gql/graphql'
import { prettyDateTime } from '@/helpers/text_formatters'
import { ResultBadge } from '../test_executions/result_badge'
import { SteppableKindBadge } from '../test_steps/steppable_kind_badge'

const ItemTestStepResultsQuery = graphql(`
  query ItemTestStepResults($filters: ItemTestStepResultFilters, $order: ItemTestStepResultOrder, $after: String) {
    itemTestStepResults(filters: $filters, order: $order, after: $after) {
      nodes {
        id
        pass
        runAt
        itemTestStep { id testStep { id name steppable { __typename } }}
        measuredValue
        errorValue
      }
      count
      pageInfo { hasNextPage hasPreviousPage startCursor endCursor }
    }
  }
`)

interface Props extends TableProps {
  filters?: ItemTestStepResultFilters
}

export const Table = ({ filters = {}, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery(ItemTestStepResultsQuery, { variables: { filters } })

  const { pageInfo } = usePageInfo(data?.itemTestStepResults?.pageInfo)

  if (loading) return <LoadingSkeleton table />

  return (
    <>
      { error && <Error error={ error } /> }
      { data && <BaseTable<ItemTestStepResultFilters, ItemTestStepResultOrder>
        { ...props }
        initialFilters={ filters }
        moreToFetch={ pageInfo.current?.hasNextPage }
        refetch={ refetch }
        totalRows={ data.itemTestStepResults.count }
        fetchMore={ () => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.itemTestStepResults.nodes = dedupe([...previousData.itemTestStepResults.nodes, ...fetchMoreResult.itemTestStepResults.nodes])
            return { ...fetchMoreResult }
          }
        }) }
        filters={ [
          {
            index: 0,
            type: 'select',
            filterKey: 'steppableEq',
            options: [
              { value: "CapacitanceTestStep", display: <SteppableKindBadge kind={"CapacitanceTestStepType"}/>},
              { value: "DiodeTestStep", display: <SteppableKindBadge kind={"DiodeTestStepType"}/>},
              { value: "HipotTestStep", display: <SteppableKindBadge kind={"HipotTestStepType"}/>},
              { value: "LinkTestStep", display: <SteppableKindBadge kind={"LinkTestStepType"}/>},
              { value: "LoadTestStep", display: <SteppableKindBadge kind={"LoadTestStepType"}/>},
              { value: "NoopTestStep", display: <SteppableKindBadge kind={"NoopTestStepType"}/>},
              { value: "RcdTestStep", display: <SteppableKindBadge kind={"RcdTestStepType"}/>},
              { value: "ResistanceTestStep", display: <SteppableKindBadge kind={"ResistanceTestStepType"}/>},
              { value: "ResistanceFourWireTestStep", display: <SteppableKindBadge kind={"ResistanceFourWireTestStepType"}/>},
              { value: "TwistedPairTestStep", display: <SteppableKindBadge kind={"TwistedPairTestStepType"}/>},
              { value: "WireTestStep", display: <SteppableKindBadge kind={"WireTestStepType"}/>},
              { value: "WireFourWireTestStep", display: <SteppableKindBadge kind={"WireFourWireTestStepType"}/>},
            ]
          },
          {
            index: 1,
            type: 'text',
            placeholder: 'Start typing...',
            filterKey: 'nameCont',
          },
          {
            index: 2,
            type: 'select',
            filterKey: 'passEq',
            options: [
              { value: true, display: <ResultBadge pass={true}/>},
              { value: false, display: <ResultBadge pass={false}/> },
            ]
          },
        ] }
        headings={ [
          { name: 'Kind' },
          { name: 'Name' },
          { name: 'Result' },
          { name: 'Run at' },
          { name: 'Error' },
          { name: 'Measurement' },
        ] }
        rows={ data?.itemTestStepResults?.nodes.map(itemTestStepResult => ({
          id: itemTestStepResult.id,
          columns: [
            <span><SteppableKindBadge kind={ itemTestStepResult.itemTestStep.testStep.steppable.__typename }/></span>,
            <span>{ itemTestStepResult.itemTestStep.testStep.name } </span>,
            itemTestStepResult.pass !== null && <ResultBadge pass={itemTestStepResult.pass}/>,
            prettyDateTime(new Date(itemTestStepResult.runAt as string)),
            <span>{ itemTestStepResult.errorValue } </span>,
            <span>{ itemTestStepResult.measuredValue } </span>,
          ],
        })) }
      /> }
    </>
  )
}
